import React, { useCallback } from 'react';
import classNames from 'classnames';
import { NativeTypes } from 'react-dnd-html5-backend';
import { useDrop } from 'react-dnd';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import ItemList from '../item-list';
import css from './drop-zone-area.module.scss';
import UploadInputState from '../upload-input-state';
import type { Item } from '../../../../types/Item';
import FileInput from '../../file-input';
import messages from '../messages';

type Props = {
  className?: string;
  intl: IntlShape;
  isDisabled?: boolean;
  items: Array<Item>;
  onAddItems: (arg1: FileList) => void;
  onRemoveItem: (arg1: Item) => void;
};

const DropZoneArea = (props: Props) => {
  const {
    items,
    onAddItems,
    isDisabled = false,
    onRemoveItem,
    className,
    intl,
    ...ariaAttrs
  } = props;

  const handleDrop = useCallback(
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS7006
    (item, monitor) => {
      if (!isDisabled && monitor) {
        const { files } = monitor.getItem();
        onAddItems(files);
      }
    },
    [isDisabled, onAddItems],
  );

  const handleRemove = (item: Item) => {
    if (!isDisabled) onRemoveItem(item);
  };

  const handleSelect = ({
    target: { files },
  }: React.ChangeEvent<HTMLInputElement>) => {
    // @ts-expect-error [MIGRATION-SUPPRESSION]: TS2345
    if (!isDisabled) onAddItems(files);
  };

  const hasItems = items.length > 0;

  const [{ canDrop, isOver }, drop] = useDrop({
    accept: [NativeTypes.FILE],
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
    drop(item: any, monitor: any) {
      if (handleDrop) {
        handleDrop(props, monitor);
      }
    },
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });

  const fullClassName = classNames(css.dropZone, className, {
    [css.dropZoneHasItems]: hasItems,
    [css.dropZoneIsOver]: !isDisabled && isOver,
    [css.dropZoneIsDisabled]: isDisabled,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!handleSelect) {
      return;
    }
    handleSelect(event);
    const currentTarget = event.currentTarget as HTMLInputElement;
    // resets the file input selection
    currentTarget.value = '';
  };

  return (
    <>
      <div {...ariaAttrs} className={fullClassName}>
        <div ref={drop} className={css.dropZoneTarget}>
          <ItemList
            isDisabled={isDisabled}
            items={items}
            onRemove={handleRemove}
          />
          <UploadInputState
            canDrop={canDrop}
            hasItems={hasItems}
            isDisabled={isDisabled}
            isOver={isOver}
            onSelect={handleSelect}
          />
        </div>

        {isDisabled ? <div className={css.dropZoneDisabledOverlay} /> : null}
      </div>
      {hasItems && (
        <div className={css.addAnotherFile}>
          <FileInput
            inputLabel={intl.formatMessage(messages.addAnotherFile)}
            isDisabled={isDisabled}
            isMultiple
            onChange={handleChange}
          />
        </div>
      )}
    </>
  );
};

export { DropZoneArea };
type WrappedProps = Omit<Props, 'intl'>;
const Wrapped = injectIntl(DropZoneArea);
export default Wrapped as React.ComponentType<WrappedProps>;
